/* global __app_id, __initial_auth_token, __firebase_config */
import React, { useState, useEffect, createContext, useContext, useRef, useCallback, useMemo } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { FixedSizeList as List } from 'react-window';
import { loadStripe } from '@stripe/stripe-js';

import { auth as firebaseAuth, db as firestoreDb, storage as firebaseStorage, functions as firebaseFunctions, rtdb } from './firebase-config';
import { ref as rtdbRef, onValue, set, onDisconnect, serverTimestamp as rtdbServerTimestamp } from 'firebase/database';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged, signInWithCustomToken, signInAnonymously, getIdToken, OAuthProvider, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { collection, addDoc, query, where, getDocs, onSnapshot, doc, deleteDoc, updateDoc, setDoc, arrayUnion, arrayRemove, orderBy, limit, startAfter, writeBatch, getDoc, serverTimestamp, increment, deleteField, documentId } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import * as LucideIcons from 'lucide-react';
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadFull } from "tsparticles";
import { Canvas, useFrame } from '@react-three/fiber';
import { Stars, OrbitControls } from '@react-three/drei';
// --- THIS IS THE FIX (Part 1): Directly import the 3D Line component from the correct library ---
import { Line as DreiLine, Ring } from '@react-three/drei';
import { Html } from '@react-three/drei';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, a } from '@react-spring/three';
import * as THREE from 'three';
import ReactPlayer from 'react-player';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Title,
    Legend,
    Filler,
    // --- NEW: Add these two for the line chart ---
    LineElement,
    PointElement,
} from 'chart.js';
import AccordionItem from './components/AccordionItem';
import useConstellationLayout from './hooks/useConstellationLayout';
import ProactiveSuggestionBanner from './components/ProactiveSuggestionBanner';
import AnonymousFeed from './components/AnonymousFeed';
import CommentSection from './components/CommentSection';
import PayoutSetupModal from './components/PayoutSetupModal';
import ConnectionCard from './components/ConnectionCard';
import { CosmicAlignmentReportModal, StarlightSurgeModal, ChartCourseModal } from './components/ConnectionModals';
import QuestBoard from './components/QuestBoard';
import WalletHub from './components/WalletHub';
import MessagesPage from './components/MessagesPage';
import UsersList from './components/UsersList';
import InteractiveConstellation from './components/InteractiveConstellation';
import UserProfile from './components/UserProfile';
import LeaderboardPage from './components/LeaderboardPage';
import HarmonyProModal from './components/HarmonyProModal';
import MyMomentsPage from './components/MyMomentsPage';
import ReelItem from './components/ReelItem';
import MomentsFeedPage from './components/MomentsFeedPage';
import CreateMomentForm from './components/CreateMomentForm';
import AdminPage from './components/AdminPage';
import Settings from './components/Settings/Settings';
import JournalEntryForm from './components/JournalEntryForm';
import NotificationsComponent from './components/NotificationsComponent';
import NexusFinder from './components/NexusFinder';
import AppSkeleton from './components/AppSkeleton';
import UniversalMediaViewer from './components/UniversalMediaViewer';
import ImmersiveIntro from './components/ImmersiveIntro';
import EmailVerificationScreen from './components/EmailVerificationScreen'; // <-- IMPORT THIS
import OnboardingFlow from './components/OnboardingFlow';
import AiToolsButton from './components/AiToolsButton';
import AiReplyGenerator from './components/AiReplyGenerator';
import LevelingGuideModal from './components/LevelingGuideModal';
import ManageConnections from './components/ManageConnections';
import ConnectionHub from './components/ConnectionHub';
import TermsOfServiceModal from './components/TermsOfServiceModal';
import InnerCircleFeedPage from './components/InnerCircleFeed';
import ChatbotAdmin from './components/ChatbotAdmin';
import NewUserChatbotPromo from './components/NewUserChatbotPromo'; // <-- IMPORT
import { badgeConfig as badgeConfigFn, collectionsConfig as collectionsConfigFn } from './components/badgeConfig';

import AuthButton from './components/AuthButton';
import { AppContext, useAppContext } from './AppContext';
import { LocationFilterProvider, useLocationFilter } from './context/LocationFilterContext';

import NotificationToast from './components/NotificationToast';
import UnreadNotificationsToast from './components/UnreadNotificationsToast';
import UniversalMediaRenderer from './components/UniversalMediaRenderer';
import SplashScreen from './components/SplashScreen';
import MarkdownRenderer from './components/MarkdownRenderer';
import ErrorBoundary from './components/ErrorBoundary';
import { BadgeProvider } from './context/BadgeContext';
import { requestNotificationPermission, deleteNotificationToken, onForegroundMessage, requestATTPermission } from './messaging';
 
import ChallengeHandler from './components/Showdown/ChallengeHandler';
import MusicPlayer from './components/MusicPlayer';
import LotteryPanel from './components/LotteryPanel';
import VisionsPanel from './components/visions/VisionsPanel';
import ProfileDropdown from './components/ProfileDropdown';
import CreateContentModal from './components/CreateContentModal';
import ScrollToTopButton from './components/ScrollToTopButton';
import { IapService } from './services/IapService';
import HarmonyHelperModal from './components/HarmonyHelperModal';
import WinnerTicker from './components/WinnerTicker';
import AppTutorial from './components/AppTutorial'; // <-- IMPORT TUTORIAL
import XPFloatingIcon from './components/XPFloatingIcon'; // <-- IMPORT XP FLOATING ICON
import AnnouncementDisplay from './components/AnnouncementDisplay'; // <-- IMPORT ANNOUNCEMENT DISPLAY
import './components/ThemeCards.css';
import BottomNavigation from './components/BottomNavigation';
import NavigationDrawer from './components/NavigationDrawer';
import { Helmet } from 'react-helmet-async';

import HoverTooltip from './components/HoverTooltip';
import MessageBox from './components/MessageBox';
import WhisperInSpace from './components/WhisperInSpace';
import AuraChamber from './components/AuraChamber';
import EchoChamber from './components/EchoChamber';
import EchoModal from './components/EchoModal';
import AddStarModal from './components/AddStarModal';
import SealedWhisperCard from './components/SealedWhisperCard';
import useIntersectionObserver from './hooks/useIntersectionObserver';
import useScrollDirection from './hooks/useScrollDirection';
import CreateNexusPersonaForm from './components/CreateNexusPersonaForm';
import NexusPage from './components/NexusPage';
import NexusList from './components/NexusList';
import NexusCard from './components/NexusCard';
import CreateNexusForm from './components/CreateNexusForm';
import NexusHub from './components/NexusHub';
import NexusHeader from './components/NexusHeader';
import ReactionBar from './components/ReactionBar';
import NexusChat from './components/NexusChat';
import NexusMembers from './components/NexusMembers';
import NexusDreamWeave from './components/NexusDreamWeave';
import NexusQuests from './components/NexusQuests';
import DreamWeavePage from './pages/DreamWeavePage';
import ShowdownPage from './pages/ShowdownPage';
import NexusFeed from './components/NexusFeed';
import MinigamePage from './pages/MinigamePage'; // Import MinigamePage
import AISpectatorPage from './pages/AISpectatorPage';
import ShowdownSpectatorPage from './pages/ShowdownSpectatorPage';
import LocationIpGuard from './components/LocationIpGuard';

// --- This line registers the components, making them available to the charts. ---
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    // --- NEW: Add these two here as well ---
    LineElement,
    PointElement
);
// --- Global Constants & Context ---

const fetchAppParameters = async () => {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve({
                worldEvents: [
                    "A new AI tool came out that makes super realistic pictures from just words. It's wild!",
                    "That famous couple, you know, the one everyone thought was perfect? They just announced they're splitting up. Shocker!",
                    "Some digital art piece sold for millions as an NFT. Like, it's just a picture on the internet, but people are going crazy for it.",
                ]
            });
        });
    });
};

const appId = typeof __app_id !== 'undefined' ? __app_id : 'whispers-of-harmony';
const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;

const TOKEN_COSTS = {
    GENERATE_PROMPT: 20,
    GET_TEASER: 30,
    GET_SIMILAR_ENTRIES: 40,
    PUBLIC_SUMMARY: 30,
    PUBLIC_SENTIMENT: 30,
    JOURNAL_SUMMARY: 40,
    SENTIMENT_ANALYSIS: 40,
    FOLLOW_UP_QUESTION: 30,
    BIO_SUMMARY: 30,
    INTEREST_ANALYSIS: 30,
    CONVERSATION_STARTER: 30,
    REVEAL_AUTHOR: 60,
    MODERATION_CHECK: 0,
    MOOD_INSIGHT: 0,
    VIBE_CHECK: 10,
    CONNECTION_COMPASS: 50,
    BIO_ENHANCER: 15,
    THEMATIC_CLOUD: 40,
    ECHOES_OF_TOMORROW: 20, // <-- ADD THIS LINE
};

const TOKEN_REWARDS = {
    DAILY_BONUS: 10,
    POST_ENTRY: 5,
    LIKE_DISLIKE_ENGAGEMENT: 0.5,
    COMMENT_ENGAGEMENT: 1,
};

const useApiCooldown = (featureName, cooldownSeconds = 60) => {
    const [isCoolingDown, setIsCoolingDown] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const storageKey = `cooldown_${ featureName } `;

    useEffect(() => {
        const lastUsed = localStorage.getItem(storageKey);
        if (lastUsed) {
            const timePassed = Date.now() - parseInt(lastUsed, 10);
            const remaining = (cooldownSeconds * 1000) - timePassed;
            if (remaining > 0) {
                setIsCoolingDown(true);
                setTimeLeft(Math.ceil(remaining / 1000));
                const interval = setInterval(() => {
                    setTimeLeft(prev => {
                        if (prev <= 1) {
                            clearInterval(interval);
                            setIsCoolingDown(false);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
                return () => clearInterval(interval);
            }
        }
    }, [featureName, cooldownSeconds, storageKey]);

    const startCooldown = useCallback(() => {
        localStorage.setItem(storageKey, Date.now().toString());
        setIsCoolingDown(true);
        setTimeLeft(cooldownSeconds);
        const interval = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setIsCoolingDown(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [cooldownSeconds, storageKey]);

    return { isCoolingDown, timeLeft, startCooldown };
};

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => { const h = setTimeout(() => setDebouncedValue(value), delay); return () => clearTimeout(h); }, [value, delay]);
    return debouncedValue;
};

const useFirestoreQuery = (query) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Using useCallback to memoize the effect's core logic
    const executeQuery = useCallback(() => {
        if (!query) {
            setData([]);
            setIsLoading(false);
            return () => { };
        }

        setIsLoading(true);
        const unsubscribe = onSnapshot(query,
            (snapshot) => {
                const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setData(docs);
                setIsLoading(false);
            },
            (err) => {
                console.error("Firestore query error:", err);
                setError(err);
                setIsLoading(false);
            }
        );
        return () => unsubscribe();
    }, [JSON.stringify(query)]); // Memoize based on the stringified query

    useEffect(() => {
        const unsubscribe = executeQuery();
        return () => unsubscribe();
    }, [executeQuery]);

    return { data, isLoading, error };
};

const extractMediaUrl = (text) => {
    if (!text) return { textWithoutUrl: '', mediaUrl: null };

    // This comprehensive regex looks for URLs from major platforms that ReactPlayer and Iframely support.
    // It's designed to find the URL even if it's surrounded by text.
    const urlRegex = /(https?:\/\/(?:www\.)?(?:instagram\.com|tiktok\.com|facebook\.com|fb\.watch|vimeo\.com|youtube\.com|youtu\.be|soundcloud\.com|dailymotion\.com|twitch\.tv|x\.com|twitter\.com)\/[^\s]+)/;
    const match = text.match(urlRegex);

    if (match && match[0]) {
        const mediaUrl = match[0];
        // Replace the found URL with an empty string and then trim any whitespace from the ends.
        const textWithoutUrl = text.replace(urlRegex, '').trim();
        return { textWithoutUrl, mediaUrl };
    }

    // If no URL is found, return the original text and a null mediaUrl.
    return { textWithoutUrl: text, mediaUrl: null };
};



// In App.js, REPLACE the existing LevelingGuideModal and ConnectionHub components with this new block.



// Helper function for Google Ads conversion tracking
const gtag_report_conversion = (url) => {
  var callback = function () {
    if (typeof(url) != 'undefined') {
      window.location = url;
    }
  };
  if (window.gtag) {
    console.log("Google Ads conversion event triggered!"); // Log for testing
    window.gtag('event', 'conversion', {
        'send_to': 'AW-988130601/upYPCN7Zh70bEKnaltcD',
        'value': 1.0,
        'currency': 'USD',
        'event_callback': callback
    });
  }
  return false;
}

// In App.js, replace the entire App() function with this corrected version.

function App() {
    const { t, i18n } = useTranslation(['translation', 'badgeconfig', 'translation_additions']);

    const badgeConfig = useMemo(() => badgeConfigFn(t), [t]);
    const collectionsConfig = useMemo(() => collectionsConfigFn(t, badgeConfig), [t, badgeConfig]);

    const Dashboard = React.lazy(() => import('./components/Dashboard'));
    const [cachedFeed, setCachedFeed] = useState(null);
    const [user, setUser] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isProfileReady, setIsProfileReady] = useState(false);
    const [showMoodInsightModal, setShowMoodInsightModal] = useState(false);
    const [moodInsightContent, setMoodInsightContent] = useState('');
    const [appParams, setAppParams] = useState(null);
    const [message, setMessage] = useState('');
    const [currentPage, setCurrentPage] = useState('anonymousFeed');
    const [selectedChatUser, setSelectedChatUser] = useState(null);
    const [userProfiles, setUserProfiles] = useState([]);
    const [userConnections, setUserConnections] = useState([]);

    const [profileToViewId, setProfileToViewId] = useState(null);
    const [onlineStatus, setOnlineStatus] = useState({});
    const [showAuraChamber, setShowAuraChamber] = useState(false);
    const [entryToScrollTo, setEntryToScrollTo] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [showProModal, setShowProModal] = useState(false);
    const [mediaToView, setMediaToView] = useState(null);
    const [nexusToViewId, setNexusToViewId] = useState(null);
    const [activeToast, setActiveToast] = useState(null);
    const [undoAction, setUndoAction] = useState(null);
    const notificationQueue = useRef([]);
    const isToastVisible = useRef(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [initialModalText, setInitialModalText] = useState('');
    const [unreadNotifications, setUnreadNotifications] = useState([]);
    const [visions, setVisions] = useState([]);
    const [showTermsOfService, setShowTermsOfService] = useState(false);
    const [showHarmonyHelper, setShowHarmonyHelper] = useState(false);
    const scrollDirection = useScrollDirection();
    const [introState, setIntroState] = useState(
        sessionStorage.getItem('hasSeenIntro') ? 'ENTERED' : 'LANDING'
    );
    const [feedType, setFeedType] = useState(() => {
        return localStorage.getItem('feedType') || 'filtered';
    }); // 'filtered' or 'global'
    const [initialWalletTab, setInitialWalletTab] = useState('hub');
    const [showChatbotPromo, setShowChatbotPromo] = useState(false);
    const [runTutorial, setRunTutorial] = useState(false);
    const [innerCircleFeedType, setInnerCircleFeedType] = useState('whispers');
    const [feedKey, setFeedKey] = useState(Date.now());
    const [isMediaWhisperActive, setIsMediaWhisperActive] = useState(false);
    const [optimisticMoments, setOptimisticMoments] = useState([]);
    const [isSecurityVerified, setIsSecurityVerified] = useState(() => sessionStorage.getItem('securityVerified') === 'true');
    const [isTopTrayExpanded, setIsTopTrayExpanded] = useState(false);

    const addOptimisticMoment = (moment) => {
        setOptimisticMoments(prev => [moment, ...prev]);
    };

    const removeOptimisticMoment = (tempId) => {
        setOptimisticMoments(prev => prev.filter(m => m.id !== tempId));
    };

    const auth = firebaseAuth;
    const db = firestoreDb;
    const storage = firebaseStorage;
    const appFunctions = firebaseFunctions;

    const signOutUser = useCallback(async () => {
        if (!auth) {
            return;
        }

        // --- FIX: Get userId directly from auth to stabilize this callback ---
        const currentUserId = auth.currentUser?.uid;
        if (currentUserId) {
            await deleteNotificationToken(currentUserId);
        }

        setIsProfileReady(false);

        try {
            await signOut(auth);

            sessionStorage.removeItem('hasSeenIntro');
            setIntroState('LANDING');
            setCurrentPage('anonymousFeed');
        } catch (e) {
            if (e.code !== 'permission-denied') {
                console.error(t('signOutError'), e);
                setMessage(t('signOutFailed', { message: e.message }));
            }
        }
    }, [auth, t, setMessage]);

    const currentUserProfile = useMemo(() => userProfiles.find(p => p.id === userId), [userProfiles, userId]);

    useEffect(() => {
        localStorage.setItem('feedType', feedType);
    }, [feedType]);

    const stripePromise = useMemo(() =>
        loadStripe('pk_live_51RozX98FT6FNb22O8VtarwAIuzw5R0nrOvB4vp9WZVi7FWOE9A9hPDTVRFPLYsCNEb8w0ig7UNssMYrBEKYGA8ss00RdhtPBhD'),
        []);

    const handleTutorialComplete = () => {
        setRunTutorial(false);
        // Optimistically update the profile in the local state
        if (currentUserProfile) {
            updateUserProfileInState(currentUserProfile.id, { hasCompletedTutorial: true });
        }
    };

    useEffect(() => {
        // Define the function on the window object so the Appilix bridge can call it
        window.receiveTokenFromNative = (token) => {
          console.log("React App: Received FCM token from native wrapper:", token);
    
          const currentUserId = auth.currentUser?.uid;
          // Simple platform detection for the wrapper
          const platform = navigator.userAgent.toLowerCase().includes('android') ? 'android' : 'ios';
    
          if (currentUserId && token) {
            console.log(`React App: User ${currentUserId} is logged in. Saving token for platform: ${platform}.`);
            const saveToken = httpsCallable(appFunctions, 'saveFcmToken');
            saveToken({ token: token, platform: platform })
              .then(() => console.log("React App: Successfully sent native FCM token to Firestore."))
              .catch((error) => console.error("React App: Error saving native FCM token:", error));
          } else {
            console.warn("React App: Could not save native token. User not logged in or token is missing.");
          }
        };
    
        // Clean up the function when the component unmounts
        return () => {
          delete window.receiveTokenFromNative;
        };
    }, [auth, appFunctions]); // Dependencies ensure auth and functions are available
    
    
    useEffect(() => {
        if (currentUserProfile?.createdAt?.toDate) { 
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
            
            // Also check if the user has already interacted with the chatbot.
            const hasInteracted = localStorage.getItem('hasInteractedWithChatbot');
            
            // Show promo only if the account is new AND they haven't interacted.
            const showPromo = currentUserProfile.createdAt.toDate() > oneWeekAgo && !hasInteracted;
            setShowChatbotPromo(showPromo);
            if (showPromo) {
                setIsTopTrayExpanded(true);
            }
        } else {
            setShowChatbotPromo(false);
        }
    }, [currentUserProfile]);

    const handleOpenCreateModalWithText = (text) => {
        setInitialModalText(text);
        setShowCreateModal(true);
    };

    const hasSetInitialLanguage = useRef(false);
    useEffect(() => {
        if (isProfileReady && currentUserProfile?.preferredLanguage && !hasSetInitialLanguage.current) {
            i18n.changeLanguage(currentUserProfile.preferredLanguage);
            hasSetInitialLanguage.current = true;
        }
    }, [isProfileReady, currentUserProfile, i18n]);
    
    useEffect(() => {
        if (isProfileReady && currentUserProfile && !currentUserProfile.country) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;

                    // --- CONVERSION TRACKING ---
                    // Check if the conversion has already been fired for this user.
                    const conversionFired = localStorage.getItem('geoConversionFired');
                    if (!conversionFired) {
                        console.log("First-time geolocation success. Reporting conversion.");
                        gtag_report_conversion();
                        // Set a flag in local storage to prevent firing again.
                        localStorage.setItem('geoConversionFired', 'true');
                    }
                    // --- END CONVERSION TRACKING ---

                    const getCountry = httpsCallable(appFunctions, 'getCountryFromCoordinates');
                    try {
                        await getCountry({ latitude, longitude });
                    } catch (error) {
                        console.error("Error getting country from coordinates:", error);
                    }
                },
                (error) => {
                    console.warn("Could not get user location:", error.message);
                }
            );
        }
    }, [isProfileReady, currentUserProfile, appFunctions]);

    useEffect(() => {
        if (user && !user.isAnonymous) {
            if (currentUserProfile) {
                setIsProfileReady(true);
            } else {
                setIsProfileReady(false);
            }
        }
    }, [user, currentUserProfile]);

    useEffect(() => {
        if (isAuthReady) { // --- FIX: Don't transition if initializing
            const hasSeenIntro = sessionStorage.getItem('hasSeenIntro') === 'true';
            if (user && !user.isAnonymous) {
                if (hasSeenIntro) {
                    setIntroState('ENTERED');
                } else {
                    setIntroState('WELCOME_BACK');
                }
            } else {
                setIntroState('LANDING');
            }
        }
    }, [user, isAuthReady]);

    const fetchVisions = useCallback(async () => {
        if (!userId || user?.isAnonymous) return; // Don't fetch if there's no user or user is anonymous
        const getVisions = httpsCallable(appFunctions, 'getVisions');
        try {
            const result = await getVisions();
            setVisions(result.data.visions);
        } catch (error) {
            console.error("Error fetching visions:", error);
        }
    }, [userId, user, appFunctions]);

    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/users/${userId}/notifications`), where("read", "==", false));
        const unsubscribeNotifications = onSnapshot(q, (snapshot) => {
            const unread = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUnreadNotifications(unread);
        });

        fetchVisions();

        return () => {
            unsubscribeNotifications();
        };
    }, [userId, db, appId, fetchVisions]);

    useEffect(() => {
        fetchVisions();
    }, [currentPage, fetchVisions]);

    const handleMarkNotificationsAsRead = useCallback(async () => {
        if (unreadNotifications.length === 0) return;

        const ids = unreadNotifications.map(n => n.id);
        const markAsReadFn = httpsCallable(appFunctions, 'markNotificationsAsRead');
        
        try {
            await markAsReadFn({ notificationIds: ids });
            // The onSnapshot listener will automatically clear the unread notifications from state
        } catch (error) {
            console.error("Error marking notifications as read:", error);
            setMessage("Could not mark notifications as read.");
        }
    }, [unreadNotifications, appFunctions, setMessage]);


    const handleEnterApp = useCallback(() => {
        if (userId) {
            requestNotificationPermission(userId);
        }
        setIntroState('ENTERED');
        sessionStorage.setItem('hasSeenIntro', 'true');
    }, [userId]);

    // --- NEW: App Tracking Transparency Request ---
    useEffect(() => {
        // Only request tracking after the user has entered the main app.
        if (introState === 'ENTERED') {
            // Use a timeout to avoid interrupting the app's entry animation.
            const timer = setTimeout(() => {
                requestATTPermission();
            }, 1000); // 1-second delay

            return () => clearTimeout(timer);
        }
    }, [introState]);

    const showConfirmation = useCallback(({ message, onConfirm, onCancel }) => {
        setConfirmation({
            message,
            onConfirm: () => { onConfirm(); setConfirmation(null); },
            onCancel: () => { if (onCancel) onCancel(); setConfirmation(null); }
        });
    }, []);

    const updateUserProfileInState = useCallback((userId, newData) => {
        setUserProfiles(prevProfiles =>
            prevProfiles.map(p =>
                p.id === userId ? { ...p, ...newData } : p
            )
        );
    }, []);

    const processQueue = useCallback(() => {
        if (isToastVisible.current || notificationQueue.current.length === 0) return;
        isToastVisible.current = true;
        const notification = notificationQueue.current.shift();
        setActiveToast(notification);
        if (notification?.id) {
            const toastRef = doc(db, `artifacts/${appId}/users/${userId}/toast_notifications`, notification.id);
            deleteDoc(toastRef).catch(e => console.error("Error deleting toast notification:", e));
        }
    }, [userId, db, appId]);

    useEffect(() => {
        if (!userId || !db) return;
        const q = query(collection(db, `artifacts/${appId}/users/${userId}/toast_notifications`), orderBy("timestamp", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    notificationQueue.current.push({ id: change.doc.id, ...change.doc.data() });
                    processQueue();
                }
            });
        });
        return () => unsubscribe();
    }, [userId, db, appId, processQueue]);

    useEffect(() => {
        if (userId && user && !user.isAnonymous && db) {
            const userProfileRef = doc(db, `artifacts/${appId}/public/data/user_profiles`, userId);
            updateDoc(userProfileRef, { lastActiveTimestamp: serverTimestamp() }).catch(err => { });
        }
    }, [userId, user, db, appId]);

    useEffect(() => {
        const handlePopState = (event) => {
            const params = new URLSearchParams(window.location.search);
            const page = params.get('page') || 'anonymousFeed';
            const targetUserId = params.get('userId');
            const chatPartnerId = params.get('chatPartnerId');
            const scrollToEntryId = params.get('scrollToEntry');
            const nexusId = params.get('nexusId');

            setCurrentPage(page);
            setProfileToViewId(targetUserId || null);
            setEntryToScrollTo(scrollToEntryId || null);
            setNexusToViewId(nexusId || null);

            if (page === 'messages' && chatPartnerId && userProfiles.length > 0) {
                const profile = userProfiles.find(p => p.id === chatPartnerId);
                if (profile) {
                    setSelectedChatUser({ id: profile.id, displayName: profile.displayName });
                } else {
                    setSelectedChatUser(null);
                }
            } else {
                setSelectedChatUser(null);
            }
        };

        window.addEventListener('popstate', handlePopState);
        
        // Initial load handling
        handlePopState();

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [userProfiles]); // Rerun if userProfiles changes, to handle direct URL loads for chats

    // --- REFACTOR: Simplified onAuthStateChanged listener to be more robust ---
    useEffect(() => {
        if (!auth || !db) {
            setIsAuthReady(true);
            return;
        }

        const signInAndSetup = async (currentUser) => {
            // Force reload the user to get the latest emailVerified status
            if (currentUser) {
                try {
                    await currentUser.reload();
                } catch (e) {
                    console.error("Error reloading user:", e);
                }
            }
            
            setUser(currentUser);
            setUserId(currentUser?.uid);

            if (currentUser) {
                IapService.initialize(currentUser.uid);
            }

            if (currentUser && !currentUser.isAnonymous) {
                onForegroundMessage(currentUser.uid);

                if (currentUser.emailVerified === false) {
                    // If user is not anonymous and email is not verified, set introState to VERIFICATION_PENDING
                    setIntroState('VERIFICATION_PENDING');
                    setIsAuthReady(true); // Still consider auth ready, but not fully into the app
                    return; // Exit early, the verification screen will handle further flow
                }

                const userProfileRef = doc(db, `artifacts/${appId}/public/data/user_profiles`, currentUser.uid);
                const docSnap = await getDoc(userProfileRef);

                if (!docSnap.exists()) {
                    // --- OPTIMISTIC UPDATE START ---
                    console.log("New user detected, performing optimistic update...");

                    // 1. Create a temporary local profile
                    const optimisticProfile = {
                        id: currentUser.uid,
                        displayName: currentUser.displayName || t('anonymousUser'),
                        tokens: 10, // Default starting tokens
                        createdAt: new Date(), // Use client-side time for now
                        // Add any other essential default fields your UI needs
                    };

                    // 2. Immediately update the local state
                    setUserProfiles(prevProfiles => [...prevProfiles, optimisticProfile]);
                    
                    // 3. Set the app as ready to render the UI
                    setIsProfileReady(true);
                    setIsAuthReady(true);
                    // --- OPTIMISTIC UPDATE END ---

                    // 4. Call the backend function in the background to create the real profile
                    const createUserProfile = httpsCallable(appFunctions, 'createUserProfile');
                    try {
                        await getIdToken(currentUser, true);
                        
                        const timestamp = Date.now();
                        const clientSecret = "ERROR_OVERLOAD_KEY";
                        const botPreventionToken = btoa(`${timestamp}:${clientSecret}`);

                        console.log("Calling createUserProfile function in the background...");
                        await createUserProfile({ 
                            displayName: currentUser.displayName || t('anonymousUser'),
                            timestamp: timestamp,
                            botPreventionToken: botPreventionToken,
                        });
                        console.log("Background profile creation function returned successfully.");
                    } catch (error) {
                        console.error(t('createUserProfileError'), error);
                        // If the background creation fails, the user might have a slightly inconsistent state.
                        // A robust solution could involve showing an error or retrying. For now, we log it.
                        signOutUser(); // Signing out might be the safest fallback.
                    }
                } else {
                    // This is an existing user, log their activity and set auth ready.
                    const logActivity = httpsCallable(appFunctions, 'logUserActivity');
                    logActivity().catch(error => console.error("Error logging user activity:", error));
                    setIsAuthReady(true);
                }
            } else {
                // This is an anonymous user.
                setIsAuthReady(true);
            }
        };

        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser) {
                setUser(null);
                setUserId(null);
                setIsProfileReady(false);
                try {
                    const cred = await signInAnonymously(auth);
                    await signInAndSetup(cred.user);
                } catch (error) {
                    console.error(t('anonymousSignInError'), error);
                    setIsAuthReady(true);
                }
            } else {
                await signInAndSetup(currentUser);
            }
        });

        return () => unsubscribe();
    }, [auth, db, appFunctions, signOutUser, t]);

    // Handle redirect result (mostly for iOS Appilix)
    useEffect(() => {
        if (!auth) return;
        getRedirectResult(auth)
            .then((result) => {
                if (result) {
                    console.log("Sign-in via redirect successful", result.user);
                    // The onAuthStateChanged listener will handle setting the user state.
                } else {
                    console.log("No redirect result found (normal page load).");
                }
            })
            .catch((error) => {
                console.error("Redirect sign-in error", error);
                if (error.code !== 'auth/popup-closed-by-user') {
                    setMessage(t('signInFailed', { message: error.message }));
                }
            });
    }, [auth, t, setMessage]);


    useEffect(() => {
        if (userId && appFunctions) {
            const recordLogin = httpsCallable(appFunctions, 'recordDailyLogin');
            recordLogin().catch(error => console.error(t('recordLoginError'), error));
        }
    }, [userId, appFunctions]);

    useEffect(() => {
        if (!rtdb) return;
        const statusRef = rtdbRef(rtdb, '/status/');
        const unsubscribe = onValue(statusRef, (snapshot) => {
            const statuses = snapshot.val() || {};
            setOnlineStatus(statuses);
        });

        return () => unsubscribe();
    }, [rtdb]);

    useEffect(() => {
        if (!userId || !rtdb) return;
        const userStatusRef = rtdbRef(rtdb, `/status/${userId}`);
        const isOnline = { isOnline: true, lastSeen: rtdbServerTimestamp() };
        const isOffline = { isOnline: false, lastSeen: rtdbServerTimestamp() };
        
        onValue(rtdbRef(rtdb, '.info/connected'), (snapshot) => {
            if (snapshot.val() === false) {
                return;
            }
            onDisconnect(userStatusRef).set(isOffline).then(() => {
                set(userStatusRef, isOnline);
                // Get and update IP address on connect
                fetch('https://api.ipify.org?format=json')
                    .then(response => response.json())
                    .then(data => {
                        const updateUserIp = httpsCallable(appFunctions, 'updateUserIp');
                        updateUserIp({ ipAddress: data.ip }).catch(error => console.error("Error updating user IP:", error));
                    })
                    .catch(error => console.error("Error fetching IP address:", error));
            });
        });

        return () => {
            set(userStatusRef, isOffline);
        };
    }, [userId, rtdb]);

    useEffect(() => {
        fetchAppParameters().then(setAppParams).catch(e => console.error(e));
    }, []);

    const uploadFile = useCallback(async (file, filePath, onProgress) => {
        if (!storage) throw new Error(t('storageNotInitialized'));
        const metadata = { contentType: file.type };
        const uploadTask = uploadBytesResumable(ref(storage, filePath), file, metadata);
        return new Promise((res, rej) => {
            uploadTask.on('state_changed', s => onProgress((s.bytesTransferred / s.totalBytes) * 100), rej, () => getDownloadURL(uploadTask.snapshot.ref).then(res).catch(rej));
        });
    }, [storage]);

    const updateUserProfile = useCallback(async (targetId, data) => {
        if (!targetId || !db) throw new Error(t('userDbNotInitialized'));
        await updateDoc(doc(db, `artifacts/${appId}/public/data/user_profiles`, targetId), data);
    }, [db, appId]);

    const updateUserTokens = useCallback(async (targetId, tokenChange) => {
        if (!db) return;
        const userProfileRef = doc(db, `artifacts/${appId}/public/data/user_profiles`, targetId);
        try {
            await updateDoc(userProfileRef, { tokens: increment(tokenChange) });
        } catch (e) {
            if (e.code === 'not-found') await setDoc(userProfileRef, { tokens: tokenChange }, { merge: true });
            else console.error(`Error updating tokens for ${targetId}:`, e);
        }
    }, [db, appId]);

    const connectUser = useCallback(async (targetId) => {
        if (!userId || !user || !db) return;
        try {
            await setDoc(doc(db, `artifacts/${appId}/users/${userId}/connections`, targetId), { followingId: targetId, timestamp: serverTimestamp() });
            await addDoc(collection(db, `artifacts/${appId}/users/${targetId}/notifications`), { type: 'CONNECTION', fromUserId: userId, fromUserName: user?.displayName || t('anonymousUser'), message: t('connectedWithMessage'), timestamp: serverTimestamp(), read: false });
            updateUserProfileInState(userId, { connections: [...(currentUserProfile?.connections || []), { followingId: targetId }] });
        } catch (e) { console.error(t('connectError'), e); setMessage(t('connectFailed', { message: e.message })); }
    }, [userId, user, db, setMessage, appId]);

    const disconnectUser = useCallback(async (targetId) => {
        if (!userId || !user || !db) return;
        try { 
            await deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/connections`, targetId));
            updateUserProfileInState(userId, { connections: currentUserProfile?.connections?.filter(c => c.followingId !== targetId) || [] });
        } 
        catch (e) { console.error(t('disconnectError'), e); setMessage(t('disconnectFailed', { message: e.message })); }
    }, [userId, user, db, setMessage, appId]);

    const generateContentWithGemini = useCallback(async (prompt) => {
        if (!appFunctions) { setMessage(t('functionsNotInitialized')); return null; }
        const currentUserProfile = userProfiles.find(p => p.id === userId);
        let modifiedPrompt = prompt;
        if (currentUserProfile?.aiMaxWordCount) {
            modifiedPrompt = `${prompt}\n\n${t('wordCountPrompt', { count: currentUserProfile.aiMaxWordCount })}`;
        }
        const callGenerateContent = httpsCallable(appFunctions, 'generateContentWithVertexAI');
        try {
            const result = await callGenerateContent({ prompt: modifiedPrompt });
            return result.data.text;
        } catch (error) {
            console.error(t('geminiError'), error);
            setMessage(t('geminiFailed', { message: error.message }));
            return null;
        }
    }, [appFunctions, setMessage, userProfiles, userId]);

    const checkContentForSafety = useCallback(async (text, isHumanEntry = false) => {
        if (!isHumanEntry || !appFunctions) return { flagged: false };
        const callModerateContent = httpsCallable(appFunctions, 'moderateContent');
        try {
            const result = await callModerateContent({ text });
            return result.data;
        } catch (error) {
            console.error(t('moderateContentError'), error);
            return { flagged: false };
        }
    }, [appFunctions]);

    const getAiGameMove = useCallback(async (gameType, dreamWeaveId, gameState) => {
        if (!appFunctions) {
            setMessage(t('functionsNotInitialized'));
            return null;
        }
        const callGetMove = httpsCallable(appFunctions, 'getAiGameMove');
        try {
            const result = await callGetMove({ gameType, dreamWeaveId, gameState });
            return result.data;
        } catch (error) {
            console.error(t('getAiGameMoveError'), error);
            setMessage(t('getAiGameMoveFailed', { message: error.message }));
            return null;
        }
    }, [appFunctions, setMessage]);

    useEffect(() => {
        if (!db) return;
        
        const q = query(collection(db, `artifacts/${appId}/public/data/user_profiles`));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setUserProfiles(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (error) => {
            console.error(t('fetchProfilesError'), error);
        });

        return () => unsubscribe(); // Cleanup the listener on component unmount
    }, [db, appId, t]);

    useEffect(() => {
        if (!db || !userId || !user || !isAuthReady || !isProfileReady) {
            setUserConnections([]);
            return;
        }
        const unsub = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/connections`), (snap) => setUserConnections(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        return () => unsub();
    }, [userId, user, db, isAuthReady, isProfileReady, appId]);

    const signInWithGoogle = useCallback(async () => {
        if (!auth) throw new Error(t('authNotInitialized'));
        
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes("Mac") && navigator.maxTouchPoints > 1);
        
        let result;
        try {
            // Force Redirect for all iOS devices to avoid WebView white screen hang.
            // This will open Safari/SFSafariViewController which is reliable.
            if (isIOS) {
                await signInWithRedirect(auth, new GoogleAuthProvider());
                return; 
            } else {
                result = await signInWithPopup(auth, new GoogleAuthProvider());
            }
        } catch (error) {
            console.error("Google Sign-in Error:", error);
            setMessage(`Sign-in Failed: ${error.message}`);
            throw error;
        }

        if (result && result.user && !result.user.emailVerified) {
            try {
                await sendEmailVerification(result.user);
                setIntroState('VERIFICATION_PENDING');
            } catch (e) {
                console.error("Failed to send verification email after Google sign-in:", e);
            }
        }
        return result;
    }, [auth, t, setMessage]);

    const signInWithApple = useCallback(async () => {
        if (!auth) throw new Error(t('authNotInitialized'));
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes("Mac") && navigator.maxTouchPoints > 1);

        let result;
        try {
            // Force Redirect for all iOS devices to avoid WebView white screen hang.
            if (isIOS) {
                await signInWithRedirect(auth, provider);
                return;
            } else {
                result = await signInWithPopup(auth, provider);
            }
        } catch (error) {
            console.error("Apple Sign-in Error:", error);
            setMessage(`Sign-in Failed: ${error.message}`);
            throw error;
        }

        if (result && result.user && !result.user.emailVerified) {
             try {
                await sendEmailVerification(result.user);
                setIntroState('VERIFICATION_PENDING'); 
            } catch (e) {
                console.error("Failed to send verification email after Apple sign-in:", e);
            }
        }
        return result;
    }, [auth, t, setMessage]);

    const signInWithEmail = useCallback(async (email, password) => {
        if (!auth) throw new Error(t('authNotInitialized'));
        const result = await signInWithEmailAndPassword(auth, email, password);
        return result;
    }, [auth]);

    const signUpWithEmail = useCallback(async (email, password) => {
        if (!auth) throw new Error(t('authNotInitialized'));
        const result = await createUserWithEmailAndPassword(auth, email, password);
        // Backend function 'sendWelcomeEmail' now handles sending the verification link
        // await sendEmailVerification(result.user); 
        return result;
    }, [auth]);



    const userOwnedNexuses = useMemo(() => {
        if (!userId || !userProfiles) return [];
        return userProfiles.filter(p => p.ownerId === userId);
    }, [userId, userProfiles]);

    const handlePageChange = useCallback((newPage, params = {}) => {
        if (!isProfileReady) return;
    
        // Special handling for lottery navigation
        if (newPage === 'lottery') {
            setCurrentPage('walletHub');
            setInitialWalletTab('lottery');

            // Update URL without page reload
            const url = new URL(window.location);
            url.searchParams.set('page', 'walletHub');
            url.searchParams.set('tab', 'lottery');
            window.history.pushState({}, '', url);
            return;
        }
    
        setProfileToViewId(null);
        setSelectedChatUser(null);
        setNexusToViewId(null);
        
        if (newPage === 'walletHub' && params.tab) {
            setInitialWalletTab(params.tab);
        } else {
            setInitialWalletTab('hub');
        }
    
        const canAccess = user && !user.isAnonymous;
        const publicPages = ['anonymousFeed', 'users', 'settings', 'publicConstellation', 'viewingProfile', 'leaderboards', 'nexus', 'moments'];
        if (!canAccess && !publicPages.includes(newPage)) {
            setMessage(t('signInToAccess'));
            return;
        }

        // Feature lock for Showdown
        const isOwner = userOwnedNexuses.length > 0;
        const releaseDate = new Date('2024-12-01');
        const isFeatureLocked = new Date() < releaseDate;

        if (newPage === 'showdown' && isFeatureLocked && !isOwner) {
            setMessage(t('showdownLocked')); // This translation key needs to be added
            return;
        }

        setCurrentPage(newPage);
        if (params.userId) setProfileToViewId(params.userId);
        if (params.nexusId) setNexusToViewId(params.nexusId);
        if (params.chatPartnerId) {
            const profile = userProfiles.find(p => p.id === params.chatPartnerId);
            if (profile) setSelectedChatUser({ id: profile.id, displayName: profile.displayName });
        } else {
            setSelectedChatUser(null);
        }
        const url = new URL(window.location);
        url.searchParams.set('page', newPage);
        ['userId', 'chatPartnerId', 'tab', 'nexusId'].forEach(p => url.searchParams.delete(p));
        for (const key in params) {
            if (params[key]) url.searchParams.set(key, params[key]);
        }
        window.history.pushState({}, '', url);

    }, [user, userProfiles, isProfileReady, t, setMessage, userOwnedNexuses]);
    const handleUserSelect = useCallback((targetUserId) => {
        if (targetUserId) handlePageChange('viewingProfile', { userId: targetUserId });
    }, [handlePageChange]);

    const handleSelectUserForMessage = useCallback((targetId, targetName) => {
        handlePageChange('messages', { chatPartnerId: targetId });
    }, [handlePageChange]);

    const handleBackToUsers = useCallback(() => {
        setSelectedChatUser(null);
        handlePageChange('messages');
    }, [handlePageChange]);

    const canAccessDashboard = currentUserProfile && ['moderator', 'admin', 'owner'].includes(currentUserProfile.role);

    const showToast = useCallback((text, type = 'info') => {
        setMessage(text);
    }, [setMessage]);

    const showUndoToast = (message, onUndo) => {
        setUndoAction({ message, onUndo });
        setTimeout(() => setUndoAction(null), 5000);
    };

    const UndoToast = () => (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-4 z-50">
            <p>{undoAction.message}</p>
            <button 
                onClick={() => {
                    undoAction.onUndo();
                    setUndoAction(null);
                }}
                className="font-bold text-yellow-400 hover:underline"
            >
                {t('undo')}
            </button>
        </div>
    );




    const appContextValue = useMemo(() => ({
        user, userId, signInWithGoogle, setMediaToView, signOutUser, updateUserProfile, connectUser, disconnectUser, userProfiles, userConnections,
        generateContentWithGemini, db, auth, storage, appFunctions, updateUserTokens, checkContentForSafety,
        getDoc, collection, addDoc, query, where, getDocs, onSnapshot, doc, deleteDoc, updateDoc, setDoc, arrayUnion, arrayRemove, orderBy, limit, startAfter, writeBatch, serverTimestamp, uploadFile, LucideIcons,
        setShowMoodInsightModal, setMoodInsightContent, setMessage, appParams, increment, deleteField, appId,
        handleUserSelect, onlineStatus, currentUserProfile, cachedFeed, setCachedFeed,
        handlePageChange, setEntryToScrollTo, entryToScrollTo, showConfirmation, setShowAuraChamber, selectedChatUser, handleSelectUserForMessage, handleBackToUsers,
        setShowProModal, stripePromise, TOKEN_COSTS, updateUserProfileInState, handleOpenCreateModalWithText,
        documentId, rtdb, badgeConfig, HoverTooltip, showToast, showUndoToast, userOwnedNexuses, visions, collectionsConfig,
        feedType, setFeedType,
        innerCircleFeedType, setInnerCircleFeedType,
        setIsMediaWhisperActive,
        optimisticMoments, addOptimisticMoment, removeOptimisticMoment
    }), [
        user, userId, signInWithGoogle, setMediaToView, signOutUser, updateUserProfile, connectUser, disconnectUser, userProfiles, userConnections,
        generateContentWithGemini, db, auth, storage, appFunctions, updateUserTokens, checkContentForSafety,
        getDoc, collection, addDoc, query, where, getDocs, onSnapshot, doc, deleteDoc, updateDoc, setDoc, arrayUnion, arrayRemove, orderBy, limit, startAfter, writeBatch, serverTimestamp, uploadFile,
        setShowMoodInsightModal, setMoodInsightContent, setMessage, appParams, increment, deleteField, appId,
        handleUserSelect, onlineStatus, currentUserProfile, cachedFeed, setCachedFeed,
        handlePageChange, setEntryToScrollTo, entryToScrollTo, showConfirmation, setShowAuraChamber, selectedChatUser, handleSelectUserForMessage, handleBackToUsers,
        setShowProModal, stripePromise, TOKEN_COSTS, updateUserProfileInState,
        documentId, rtdb, showToast, showUndoToast, userOwnedNexuses, visions, badgeConfig, collectionsConfig,
        feedType,
        innerCircleFeedType, setInnerCircleFeedType,
        setIsMediaWhisperActive,
        optimisticMoments
    ]);

    useEffect(() => {
        if (user && !user.isAnonymous && currentUserProfile) {
            if (!currentUserProfile.termsAccepted) {
                const localStorageAccepted = localStorage.getItem('termsAccepted');
                if (!localStorageAccepted) {
                    setShowTermsOfService(true);
                }
            }
        }
    }, [user, currentUserProfile]);

    const handleAcceptTerms = async () => {
        if (userId) {
            const userProfileRef = doc(db, `artifacts/${appId}/public/data/user_profiles`, userId);
            await updateDoc(userProfileRef, { termsAccepted: true });
            localStorage.setItem('termsAccepted', 'true');
            setShowTermsOfService(false);
        }
    };

    if (!isAuthReady || !appParams) {
        return <SplashScreen />;
    }

        const mainAppContent = (
            <ErrorBoundary>
                <>
                    <Helmet>
                        <title>Whispers of Harmony</title>
                        <meta name="description" content="The world's best gamified Social network experience. Connect, express, and evolve alongside your AI." />
                        <meta name="theme-color" content="#0f1115" />
                    </Helmet>
                    <PayPalScriptProvider options={{ 
                        "client-id": "AV_SaODc_D66O66nQyMz5GN98n9F0ZXhRHkS-A1eJpN1Wdq6f9sc_bkASy_0qJNPtb9KviK5YwqLgmqs",
                        "vault": true 
                    }}>
                        {showTermsOfService && <TermsOfServiceModal onAccept={handleAcceptTerms} />}
                        <AnnouncementDisplay />
                        <XPFloatingIcon />
                        <NavigationDrawer 
                            isOpen={isDrawerOpen} 
                            onClose={() => setIsDrawerOpen(false)} 
                            handlePageChange={(page) => {
                                handlePageChange(page);
                                setIsDrawerOpen(false);
                            }} 
                            currentPage={currentPage}
                            user={user}
                            LucideIcons={LucideIcons}
                            unreadNotificationsCount={unreadNotifications.length}
                            canAccessDashboard={canAccessDashboard}
                            t={t}
                        />
                        <div className="min-h-screen text-gray-100 relative flex flex-col transition-colors duration-300 font-inter">
                            {runTutorial ? (
                                <AppTutorial onTutorialComplete={handleTutorialComplete} />
                            ) : (
                                <>
                                    {activeToast && (
                                        <NotificationToast
                                            notification={activeToast}
                                            onClose={() => {
                                                setActiveToast(null);
                                                isToastVisible.current = false;
                                                processQueue(); // Check for the next one
                                            }}
                                            onNavigate={(n) => handlePageChange(n.navigation.page, n.navigation.params)}
                                        />
                                    )}
                                    {undoAction && <UndoToast />}
                                    {confirmation && <MessageBox message={confirmation.message} onConfirm={confirmation.onConfirm} onClose={confirmation.onCancel} showConfirm={true} />}
                                    {mediaToView && <UniversalMediaViewer mediaData={mediaToView} onClose={() => setMediaToView(null)} LucideIcons={LucideIcons} />}
                                    {showProModal && <HarmonyProModal onClose={() => setShowProModal(false)} />}
                                    {showCreateModal && <CreateContentModal 
                                        onClose={() => {
                                            setShowCreateModal(false); 
                                            setInitialModalText(''); // Clear initial text on close
                                        }} 
                                        initialText={initialModalText} 
                                        onPostSuccess={() => setFeedKey(Date.now())}
                                    />}
                                    {showHarmonyHelper && <HarmonyHelperModal onClose={() => setShowHarmonyHelper(false)} />}
                                    {!isMediaWhisperActive && !['moments', 'myMoments'].includes(currentPage) && (
                                        <>
                                            <LotteryPanel />
                                        </>
                                    )}
                                    
                                    {!['moments', 'myMoments'].includes(currentPage) && (
                                        <>
                                            {/* Top navigation toggle removed in favor of bottom nav */}
    
                                            {!isMediaWhisperActive && (
                                                <div className="fixed top-0 left-0 right-0 z-40 flex flex-col items-center pointer-events-none">
                                                    <AnimatePresence>
                                                        {isTopTrayExpanded && (
                                                            <motion.div
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                                className="w-full bg-gray-950/85 border-b border-white/10 backdrop-blur-md px-4 py-3 flex flex-col gap-3 pointer-events-auto"
                                                            >
                                                                {/* Row 1: Nav Bar Controls */}
                                                                <div className="w-full flex items-center justify-between">
                                                                    {/* Left side: Music Player & Chatbot Button */}
                                                                    <div className="flex-1 flex items-center gap-3">
                                                                        {currentPage !== 'innerCircleFeed' && <MusicPlayer LucideIcons={LucideIcons} />}
                                                                        <button
                                                                            onClick={() => setShowHarmonyHelper(true)}
                                                                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/25 hover:bg-blue-600/40 border border-blue-500/30 hover:border-blue-400/50 rounded-full text-blue-300 hover:text-white font-semibold text-xs font-orbitron transition-all duration-300 cursor-pointer pointer-events-auto"
                                                                            title="Ask Cosmic Chatbot"
                                                                        >
                                                                            <LucideIcons.MessageSquare size={14} className="text-blue-400" />
                                                                            <span className="hidden sm:inline">Ask AI</span>
                                                                        </button>
                                                                    </div>
                                                                    {/* Center: App Title */}
                                                                    <div className="flex-1 flex justify-center">
                                                                        {currentPage === 'innerCircleFeed' ? (
                                                                            <div className="flex items-center bg-gray-800/70 backdrop-blur-sm rounded-full p-1">
                                                                                <button
                                                                                    onClick={() => setInnerCircleFeedType('whispers')}
                                                                                    className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors duration-300 ${innerCircleFeedType === 'whispers' ? 'bg-blue-500 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                                                                                >
                                                                                    {t('nav.feed')}
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => setInnerCircleFeedType('moments')}
                                                                                    className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors duration-300 ${innerCircleFeedType === 'moments' ? 'bg-purple-500 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                                                                                >
                                                                                    {t('nav.moments')}
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex items-center gap-2">
                                                                                <img src="/logo.png" alt="" className="w-6 h-6 object-contain drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]" />
                                                                                <span className="text-base sm:text-lg font-bold font-orbitron tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                                                                                    Whispers of Harmony
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {/* Right side: User Info */}
                                                                    <div className="flex-1 flex justify-end">
                                                                        {currentUserProfile && (
                                                                            <div className="flex flex-col-reverse sm:flex-row items-end sm:items-center space-y-2 space-y-reverse sm:space-y-0 sm:space-x-4">
                                                                                <div className="p-2 bg-gray-800/70 backdrop-blur-sm rounded-full flex items-center space-x-2 text-white font-bold text-sm">
                                                                                    <LucideIcons.Gem size={16} className="text-purple-400" />
                                                                                    <span>{currentUserProfile.tokens || 0}</span>
                                                                                </div>
                                                                                <ProfileDropdown />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Row 2: Visions / Stories (only on Feed pages) */}
                                                                {!isMediaWhisperActive && ['anonymousFeed', 'innerCircleFeed', 'connectedFeed'].includes(currentPage) && (
                                                                    <div className="w-full border-t border-white/5 pt-2">
                                                                        <VisionsPanel visions={visions} handleUserSelect={handleUserSelect} />
                                                                    </div>
                                                                )}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
 
                                                    <button
                                                        onClick={() => setIsTopTrayExpanded(!isTopTrayExpanded)}
                                                        className="pointer-events-auto bg-gray-900/80 border-b border-x border-white/10 hover:border-purple-500/30 hover:bg-gray-950 backdrop-blur-md text-gray-400 hover:text-white px-6 py-1 rounded-b-2xl shadow-lg flex items-center justify-center gap-1.5 transition-all duration-300 group cursor-pointer"
                                                        title={isTopTrayExpanded ? "Collapse Menu" : "Expand Menu"}
                                                    >
                                                        {isTopTrayExpanded ? (
                                                            <>
                                                                <LucideIcons.ChevronUp size={14} className="group-hover:-translate-y-0.5 transition-transform text-purple-400" />
                                                                <span className="text-[10px] uppercase font-bold tracking-[0.2em] font-orbitron opacity-70 group-hover:opacity-100 transition-opacity">Hide</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <LucideIcons.ChevronDown size={14} className="group-hover:translate-y-0.5 transition-transform text-cyan-400" />
                                                                <span className="text-[10px] uppercase font-bold tracking-[0.2em] font-orbitron opacity-70 group-hover:opacity-100 transition-opacity">Menu</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
 
                                            <ChallengeHandler handlePageChange={handlePageChange} />
                                            <ScrollToTopButton />
                                            
                                            {/* Custom FAB removed in favor of Create button in bottom nav */}
                                            {!isMediaWhisperActive && showChatbotPromo && <NewUserChatbotPromo />}
                                        </>
                                    )}
                                    
                                    {showAuraChamber && <AuraChamber onClose={() => setShowAuraChamber(false)} />}
                                    <BottomNavigation 
                                        currentPage={currentPage}
                                        handlePageChange={handlePageChange}
                                        unreadMessages={currentUserProfile?.hasUnreadMessages}
                                        user={user}
                                        userId={userId}
                                        setShowCreateModal={setShowCreateModal}
                                        setIsDrawerOpen={setIsDrawerOpen}
                                    />
                    
                                    <div className={`relative flex flex-col flex-grow min-h-0 h-full ${!['anonymousFeed', 'innerCircleFeed', 'connectedFeed', 'moments', 'myMoments', 'showdown', 'spectator', 'showdownSpectator'].includes(currentPage) ? 'pt-24 p-4 sm:p-8' : 'p-0'}`}>
                                        {!['anonymousFeed', 'innerCircleFeed', 'connectedFeed', 'moments', 'myMoments', 'showdown', 'spectator', 'showdownSpectator'].includes(currentPage) && (
                                            <header className="text-center mb-12 flex-shrink-0 flex flex-col items-center mt-4">
                                                <div className="relative mb-2 transition-all duration-700 hover:scale-105 cursor-default drop-shadow-[0_0_30px_rgba(139,92,246,0.4)]">
                                                    <img src="/logo.png" alt="Whispers of Harmony Logo" className="w-32 sm:w-48 md:w-56 h-auto" />
                                                </div>
                                                <div className="relative z-10 mb-2 mt-[-1rem]">
                                                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold font-playfair tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 drop-shadow-lg">
                                                        Whispers of Harmony
                                                    </h1>
                                                </div>
                                                <p className="font-sans text-xs sm:text-sm md:text-base text-cyan-100/70 tracking-[0.3em] uppercase font-semibold drop-shadow-sm mt-2">
                                                    Connect, Create, and Explore Your Inner Universe
                                                </p>
                                            </header>
                                        )}
                    
                                        <main className={`${!['moments', 'myMoments', 'showdown', 'spectator', 'showdownSpectator'].includes(currentPage) ? 'max-w-7xl mx-auto' : ''} w-full flex-grow min-h-0`}>
                                            <div className="lg:grid lg:grid-cols-12 lg:gap-8 h-full">
                                                <div className="lg:col-span-12 h-full feed-container"> {/* <-- CLASS FOR TUTORIAL */}
                                                    <div key={currentPage} className="h-full">
                                                        <ErrorBoundary showDetails={true}>
                                                            {message && <MessageBox message={message} onClose={() => setMessage('')} />}

                                                            {currentPage === 'anonymousFeed' && <AnonymousFeed key={feedKey} />}
                                                            {currentPage === 'innerCircleFeed' && user && !user.isAnonymous && <InnerCircleFeedPage />}
                                                            {currentPage === 'createMoment' && user && !user.isAnonymous && <CreateMomentForm />}
                                                            {currentPage === 'moments' && <MomentsFeedPage />}
                                                            {currentPage === 'myMoments' && user && !user.isAnonymous && <MyMomentsPage />}
                                                            {currentPage === 'walletHub' && user && userId && <WalletHub initialTab={initialWalletTab} />}
                                                            {currentPage === 'newEntry' && user && !user.isAnonymous && <JournalEntryForm />}
                                                            {currentPage === 'users' && <UsersList onUserSelect={handleUserSelect} />}
                                                            {currentPage === 'leaderboards' && <LeaderboardPage />}
                                                            {currentPage === 'viewingProfile' && profileToViewId && (<UserProfile profileUserId={profileToViewId} onMessageUser={handleSelectUserForMessage} onToggleConnection={(targetId, isCurrentlyConnected) => { 
                                                                if (isCurrentlyConnected) { disconnectUser(targetId); } else { connectUser(targetId); } 
                                                            }} />)}
                                                            {currentPage === 'connectedFeed' && user && userId && <ConnectionHub />}
                                                            {currentPage === 'publicConstellation' && profileToViewId && <InteractiveConstellation publicUserId={profileToViewId} />}
                                                            {currentPage === 'messages' && user && userId && <MessagesPage />}
                                                            {currentPage === 'notifications' && <NotificationsComponent />}
                                                            {currentPage === 'dashboard' && canAccessDashboard && <React.Suspense fallback={<AppSkeleton />}><Dashboard /></React.Suspense>}
                                                            {currentPage === 'chatbotAdmin' && canAccessDashboard && <ChatbotAdmin />}
                                                            {currentPage === 'settings' && <Settings />}
                                                            {currentPage === 'admin' && canAccessDashboard && <AdminPage />}
                                                            {currentPage === 'nexus' && nexusToViewId && <NexusHub nexusId={nexusToViewId} />}
                                                            {currentPage === 'nexus' && !nexusToViewId && <NexusPage />}
                                                            {currentPage === 'dreamweave' && <DreamWeavePage />}
                                                            {currentPage === 'showdown' && <ShowdownPage />}
                                                            {currentPage === 'spectator' && <AISpectatorPage />}
                                                            {currentPage === 'showdownSpectator' && <ShowdownSpectatorPage />}
                                                            {currentPage === 'minigamePage' && <MinigamePage />} {/* New MinigamePage route */}
                                                        </ErrorBoundary>
                                                    </div>
                                                </div>
                                            </div>
                                        </main>
                    
                                        {!['moments', 'myMoments', 'showdown', 'spectator', 'showdownSpectator'].includes(currentPage) && (
                                            <>
                                                <WinnerTicker />
                                                <footer className="text-center mt-10 text-gray-700 text-sm flex-shrink-0">
                                                    <p>Copyright © {new Date().getFullYear()}</p>
                                                </footer>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </PayPalScriptProvider>
                </>
            </ErrorBoundary>
        );

    return (
        <AppContext.Provider value={appContextValue}>
            <BadgeProvider>
                <LocationFilterProvider>
                    {/* Main Conditional Render for entire app content */}
                    {
                        // Condition 1: User is logged in, not anonymous, and email is NOT verified OR introState is VERIFICATION_PENDING
                        (user && !user.isAnonymous && user.emailVerified === false) || introState === 'VERIFICATION_PENDING' ? (
                            <EmailVerificationScreen user={user} onLogout={signOutUser} />
                        ) : (
                            // Condition 2: If not in verification pending state, check introState
                            introState !== 'ENTERED' ? (
                                <ImmersiveIntro
                                    introState={introState}
                                    onEnter={handleEnterApp}
                                    onShowSignIn={() => setIntroState('SIGN_IN')}
                                    onSignIn={signInWithGoogle}
                                    signInWithApple={signInWithApple}
                                    signInWithEmail={signInWithEmail}
                                    signUpWithEmail={signUpWithEmail}
                                    onBack={() => setIntroState('LANDING')}
                                />
                            ) : (
                                // Condition 3: If entered, check profile readiness/banned status
                                (isProfileReady || (user && user.isAnonymous)) ? (
                                    (user && !user.isAnonymous && !currentUserProfile?.hasCompletedOnboarding) ? (
                                        <OnboardingFlow 
                                            onComplete={() => {
                                                updateUserProfileInState(currentUserProfile.id, { hasCompletedOnboarding: true });
                                            }} 
                                        />
                                    ) :
                                    currentUserProfile?.status === 'banned' ? (
                                        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-8 flex items-center justify-center transition-colors duration-300 font-inter">
                                            <div className="bg-red-900/80 backdrop-blur-md text-white p-8 rounded-lg shadow-lg text-center border border-red-500">
                                                <LucideIcons.Ban size={48} className="mx-auto mb-4 text-red-400" />
                                                <h1 className="text-3xl font-bold text-red-300">Banned</h1>
                                                <p className="mt-4">You have been banned from using this application.</p>
                                                {currentUserProfile.banReason && <p className="mt-2 text-sm bg-black/20 p-2 rounded-md">Reason: {currentUserProfile.banReason}</p>}<button onClick={signOutUser} className="mt-6 small-action-button bg-gray-600 hover:bg-gray-700">Sign Out</button>
                                            </div>
                                        </div>
                                    ) : (
                                        !isSecurityVerified ? (
                                            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-300 font-inter">
                                                <div className="bg-black/60 backdrop-blur-md p-8 rounded-xl border border-blue-500/30 shadow-2xl max-w-lg w-full">
                                                    <LocationIpGuard 
                                                        onPass={() => {
                                                            setIsSecurityVerified(true);
                                                            sessionStorage.setItem('securityVerified', 'true');
                                                        }}
                                                        onRefuse={() => {
                                                            // Reloading keeps the user in the app but resets the state
                                                            window.location.reload(); 
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            mainAppContent
                                        )
                                    )
                                ) : (
                                    <SplashScreen />
                                )
                            )
                        )
                    }
                </LocationFilterProvider>
            </BadgeProvider>
        </AppContext.Provider>
    );
}

export default App;
